/* eslint-disable no-unused-vars */
/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, createContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

// Component imports
import { Alert, Button, Col, Divider, Grid, PageHeader, Row, Space, Tabs, Tag, Typography } from 'antd';
import { CustomerServiceOutlined, ContainerOutlined, DisconnectOutlined, SettingOutlined } from '@ant-design/icons';
import DocumentTitle from '../components/DocumentTitle';
import LiveChatAgentSettingsForm from '../components/Forms/LiveChatAgentSettingsForm';
import LiveChatLayout from '../components/LiveChat/LiveChatLayout';
import Permissioned from '../components/Permissioned';
import { ConversationIcon } from '../components/Icons/ConversationIcon';
import { RequestIcon } from '../components/Icons/RequestIcon';
import LiveChatSettingsScreen from '../screens/LiveChatSettingsScreen';

// Misc imports
import hasLiveChatAccess from '../utils/misc/hasLiveChatAccess';
import { ADDITIONAL_ROLES_ORG, PERMISSIONS_ORG, PLATFORMS, STORAGE_KEYS } from '../constants/main';
import { NOTIFICATIONS_PERMISSIONS } from '../constants/notifications';
import { LIVECHAT_MESSAGE_TYPES, LIVECHAT_PARTICIPANT_TYPES, LIVECHAT_SCREEN_TABS, REQUEST_STATUSES } from '../constants/liveChat';
import is from '../utils/is';
import { sendConversationMessage, toggleNotificationSound, togglePushNotifications } from '../store/liveChat/liveChatThunks';
import { useFetchLiveChatApplicationSettingsQuery } from '../store/liveChatSettings/liveChatSettingsApi';
import { useFetchUserGroupsQuery } from '../store/userGroups/userGroupsApi';
import { useFetchUsersThinListQuery } from '../store/users/usersApi';
import { useFetchAutomationsQuery, useFetchPredefinedResponsesQuery } from '../store/predefinedEntities/predefinedEntitiesApi';
import { getCurrentQueryState } from '../store/utils/getCurrentQueryState';
import { useFakeConversationMessageMutation, useFakeLivechatRequestMutation, useFetchActiveConversationQuery, useFetchRequestsQuery } from '../store/liveChat/liveChatApi';
import replaceUrlParams from '../utils/string/replaceUrlParams';
import { NAV_LIVECHAT } from '../constants/navigation';
import useUserPermissions from '../utils/hooks/useUserPermissions';
import { LiveChatIcon } from '../components/Icons/LiveChatIcon';

// Constant declarations
const { useBreakpoint } = Grid;
const LIVECHAT_ADMIN_PERMISSIONS = [PERMISSIONS_ORG.ADMIN, ADDITIONAL_ROLES_ORG.LIVECHAT_ADMIN];

const TABS_TO_DISPLAY_NEW_CLOSED_OPEN_BUTTONS = [LIVECHAT_SCREEN_TABS.CLOSED, LIVECHAT_SCREEN_TABS.NEW, LIVECHAT_SCREEN_TABS.OPEN];

export const LiveChatContext = createContext();

const LiveChatScreen = () => {
  const { orgId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobileSize = (screens.xs || screens.sm) && !screens.md;
  const isDesktopSize = screens.xl;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userPermissions = useUserPermissions(orgId, null);

  const { currentUser } = useSelector((state) => state.auth);
  const { isAuthorized, isConnected, isBeingAuthorized, isEstablishingConnection } = useSelector((state) => state.livechat);
  const { currentData: incomingRequests } = getCurrentQueryState('fetchRequests', { orgId });
  const { currentData: activeConversationInfo } = useFetchActiveConversationQuery({ orgId, conversationId: searchParams.get('conversation') }, { skip: isNaN(searchParams.get('conversation')) || !searchParams.get('conversation') });
  const { currentData: _newRequests } = useFetchRequestsQuery({ orgId, urlParams: { skip: 0, take: 10, status: REQUEST_STATUSES.NEW } }, { skip: !isConnected }); // Fetch new requests so that we can show the counter badge

  const { data: _livechatApplicationSettings } = useFetchLiveChatApplicationSettingsQuery({ orgId });
  const { data: _users } = useFetchUsersThinListQuery({ orgId, urlParams: { organizationRole: ADDITIONAL_ROLES_ORG.LIVE_AGENT } });
  const { data: userGroups } = useFetchUserGroupsQuery({ orgId, urlParams: { pageSize: 500 } });
  const { data: _automations } = useFetchAutomationsQuery({ orgId, urlParams: { tenantScope: activeConversationInfo?.tenantId } }, { skip: !activeConversationInfo?.tenantId });
  const { data: _predefinedResponses } = useFetchPredefinedResponsesQuery({ orgId, urlParams: { tenantScope: activeConversationInfo?.tenantId } }, { skip: !activeConversationInfo?.tenantId });
  const [fakeConversationMessage] = useFakeConversationMessageMutation();
  const [fakeLivechatRequest] = useFakeLivechatRequestMutation();

  const [liveAgentGroups, setLiveAgentGroups] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [newRequests, setNewRequests] = useState(0);
  const [isEditingResponses, setIsEditingResponses] = useState(false);
  const [isEditingAutomations, setIsEditingAutomations] = useState(false);
  const [previousUrlParams, setPreviousUrlParams] = useState(new URLSearchParams());
  const [currentUrlParams, setCurrentUrlParams] = useState(new URLSearchParams(location.search));

  let currentTab = searchParams.get('tab') || LIVECHAT_SCREEN_TABS.NEW;

  useEffect(() => {
    // Check for sound notification preferneces
    if (window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_CONVERSATIONS_SOUND) === null) {
      dispatch(toggleNotificationSound(true));
    }
    if (window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_REQUESTS_SOUND) === null) {
      dispatch(toggleNotificationSound(true, true));
    }

    // Initialize the layout preferences in local storage
    if (!window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_LAYOUT_PREFERENCES)) {
      window.localStorage.setItem(STORAGE_KEYS.LIVECHAT_LAYOUT_PREFERENCES, JSON.stringify({}));
    } else {
      const layoutConfig = JSON.parse(window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_LAYOUT_PREFERENCES));
      setSidebarOpen(layoutConfig.sidebarOpen);
    }

    // Ask user permission for browser notifications
    if (window.Notification && Notification.permission === NOTIFICATIONS_PERMISSIONS.DEFAULT) {
      try {
        Notification.requestPermission().then(choice => {
          dispatch(togglePushNotifications(choice));
        });
      } catch (error) { /* Do nothing */ }
    }
  }, []);

  useEffect(() => {
    // If the user is a livechat admin but not a live agent, then redirect to the admin panel tab
    if (searchParams.get('tab') === LIVECHAT_SCREEN_TABS.OPEN) {
      if (LIVECHAT_ADMIN_PERMISSIONS.some(permission => userPermissions.includes(permission)) && !userPermissions.includes(ADDITIONAL_ROLES_ORG.LIVE_AGENT)) {
        setSearchParams({ tab: LIVECHAT_SCREEN_TABS.ADMIN_SETTINGS });
      }
    }
  }, [location.search]);

  useEffect(() => {
    const layoutConfig = JSON.parse(window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_LAYOUT_PREFERENCES)) ?? {};
    layoutConfig.sidebarOpen = sidebarOpen;
    window.localStorage.setItem(STORAGE_KEYS.LIVECHAT_LAYOUT_PREFERENCES, JSON.stringify(layoutConfig));
  }, [sidebarOpen]);

  useEffect(() => {
    if (isMobileSize && sidebarOpen) {
      setSidebarOpen(false);
    }
  }, [isMobileSize]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    // Track previously visited tabs to properly exit settings/admin panel
    setCurrentUrlParams(urlSearchParams);
    // Retain previous tab when user is inside settings
    if (![LIVECHAT_SCREEN_TABS.SETTINGS, LIVECHAT_SCREEN_TABS.ADMIN_SETTINGS].includes(currentUrlParams.get('tab'))) {
      setPreviousUrlParams(currentUrlParams);
    }
    // Setting up the initial state based on the URL search query
    if (!Object.values(LIVECHAT_SCREEN_TABS).includes(urlSearchParams.get('tab'))) {
      setSearchParams({ tab: LIVECHAT_SCREEN_TABS.OPEN });
    }
  }, [location.search]);

  useEffect(() => {
    if (location.state) {
      const { request } = location.state;
      if (request) {
        setSelectedRequest(request);
        if (!isDesktopSize) {
          setInboxOpen(false);
          setSidebarOpen(false);
        }
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (userGroups) {
      setLiveAgentGroups(userGroups.items.filter(group => group.organizationRoles.includes(ADDITIONAL_ROLES_ORG.LIVE_AGENT)));
    }
  }, [userGroups]);

  useEffect(() => {
    if (incomingRequests && incomingRequests.data) {
      let requestCount = 0;
      for (const request of incomingRequests.data) {
        if (request.status === REQUEST_STATUSES.NEW) {
          requestCount += 1;
        }
        if (requestCount > 10) {
          break;
        }
      }
      setNewRequests(requestCount);
    }
  }, [incomingRequests]);

  const getMostRecentRequest = (requests) => {
    /*
      It accepts a list of requests and return the most recent one
      If it doesn't exist return null
    */
    if (!requests || !requests.length) {
      return null;
    }

    const recentRequest = requests[0];
    if (recentRequest) {
      return recentRequest;
    } else {
      return null;
    }
  };

  const getFilteredRequestsAccordingToStatus = (requests, status) => {
    /*
      It accepts a list of requests and filters according to status
      Returns a list containing the requests with the status provided
    */
    const filteredList = requests?.filter(request => request.status === status);
    return filteredList;
  };

  const setMostRecentRequestAsSelectedAccordingToStatus = (incomingRequests, status) => {
    /*
      It accepts a list of requests and a status
      If the most recent request of a required status exists then set it as Selected
      If not then do nothing
    */
    const filteredRequests = getFilteredRequestsAccordingToStatus(incomingRequests, status);
    const requiredRequest = getMostRecentRequest(filteredRequests);
    if (requiredRequest) {
      setSelectedRequest(requiredRequest);
    }
  };

  const fakeRequest = () => {
    fakeLivechatRequest({ data: {
      platformSessionId: uuidv4(),
      platformUserName: currentUser.fullName,
      platformType: PLATFORMS.WEBCHAT.key,
      platformUserId: uuidv4(),
      tenantName: 'Helvia testing bot',
      organizationId: orgId,
      tenantId: '6052104673c0fe08d75193d7',
      tenantImageUrl: 'https://res.cloudinary.com/helvia-io/image/upload/v1618302624/Helvia%20logo/Helvia-icon-192x192-fgwhite-bgblack.png'
    } });
  };

  const renderConnectionAuthorizationErrors = () => {
    if (!isConnected && isEstablishingConnection === false) {
      return (
        <Alert
          type="error"
          className="mb-1"
          message={
            <Typography.Text>
              <DisconnectOutlined className="mr-2" />
              {t('Connection with the LiveChat server has been lost. Please try again later.')}
            </Typography.Text>
          }
        />
      );
    }

    if (!isAuthorized && isBeingAuthorized === false) {
      return (
        <Alert
          type="error"
          className="mb-1"
          message={
            <Typography.Text>
              <DisconnectOutlined className="mr-2" />
              {t('You\'re not authorized to communicate with the LiveChat server. Please try refreshing the page.')}
            </Typography.Text>
          }
        />
      );
    }
    return;
  };

  const renderPageTitle = () => {
    switch (currentTab) {
      case LIVECHAT_SCREEN_TABS.NEW:
        return t('LiveChat Inbox - New');
      case LIVECHAT_SCREEN_TABS.OPEN:
        return t('LiveChat Inbox - Open');
      case LIVECHAT_SCREEN_TABS.CLOSED:
        return t('LiveChat Inbox - Closed');
      case LIVECHAT_SCREEN_TABS.REQUESTS_HISTORY:
        return t('LiveChat Requests History');
      case LIVECHAT_SCREEN_TABS.SETTINGS:
        return t('My Settings');
      case LIVECHAT_SCREEN_TABS.ADMIN_SETTINGS:
        return t('LiveChat Admin Panel');
      default:
        return t('LiveChat');
    }
  };

  const shouldHideMainLayout = [LIVECHAT_SCREEN_TABS.SETTINGS, LIVECHAT_SCREEN_TABS.ADMIN_SETTINGS].includes(currentTab);

  return (
    <Fragment>
      <DocumentTitle title={renderPageTitle()} />

      {renderConnectionAuthorizationErrors()}

      <Permissioned
        allowedPermissions={[true]}
        currentPermissions={[hasLiveChatAccess(currentUser, orgId)]}
      >
        {
          is.dev() && !shouldHideMainLayout ? (
            <Row
              align="middle"
              gutter={24}
            >
              <Col>
                <Space className="mb-2 w-100 justify-content-end" wrap={true}>
                  <Button
                    size="small"
                    type="primary"
                    className="pb-5"
                    onClick={() => fakeRequest()}
                  >
                    {t('Fake request')}
                  </Button>

                  <Button
                    size="small"
                    type="primary"
                    className="pb-5"
                    disabled={!activeConversationInfo}
                    onClick={() => fakeConversationMessage({ orgId, data: {
                      livechatEndUser: {
                        platformUserId: activeConversationInfo.livechatParticipants.find(participant => participant.participantType === LIVECHAT_PARTICIPANT_TYPES.ENDUSER).platformUserId
                      },
                      text: 'Hey are you there?'
                    } })}
                  >
                    {t('Fake message')}
                  </Button>

                  <Button
                    size="small"
                    type="primary"
                    className="pb-5"
                    disabled={!activeConversationInfo}
                    onClick={() => dispatch(sendConversationMessage({
                      text: 'This is a test system message',
                      livechatConversation: {
                        id: activeConversationInfo?.id
                      },
                      type: LIVECHAT_MESSAGE_TYPES.SYSTEM_MESSAGE
                    }))}
                  >
                    {t('Fake system message')}
                  </Button>
                </Space>
              </Col>
            </Row>
          ) : null
        }

        <LiveChatContext.Provider
          value={{
            isMobileSize,
            liveAgentGroups,
            sidebarOpen,
            setSidebarOpen,
            inboxOpen,
            setInboxOpen,
            selectedRequest,
            setSelectedRequest,
            isEditingResponses,
            setIsEditingResponses,
            isEditingAutomations,
            setIsEditingAutomations,
            previousUrlParams
          }}
        >
          {shouldHideMainLayout ? (
            <div>
              {currentTab === LIVECHAT_SCREEN_TABS.SETTINGS ? <LiveChatAgentSettingsForm /> : <LiveChatSettingsScreen />}
            </div>
          )
            : (<Tabs
              activeKey={currentTab}
              onTabClick={(key) => {
                setMostRecentRequestAsSelectedAccordingToStatus(incomingRequests?.data, REQUEST_STATUSES.NEW);
                setSearchParams({ tab: key });
                setSidebarOpen(false);

                if (isMobileSize && (key === LIVECHAT_SCREEN_TABS.NEW || key === LIVECHAT_SCREEN_TABS.OPEN)) {
                  setInboxOpen(true);
                }
              }}
              items={[
                ...(TABS_TO_DISPLAY_NEW_CLOSED_OPEN_BUTTONS.includes(currentTab)
                  ? [
                    {
                      key: LIVECHAT_SCREEN_TABS.NEW,
                      label: (
                        <span>
                          <RequestIcon hasNewRequests={is.truthy(newRequests)}/>
                          {t('New')} <span className="text-error">{newRequests ? newRequests > 9 ? '(9+)' : `(${newRequests})` : null}</span>
                        </span>
                      ),
                      children: <LiveChatLayout />
                    },
                    {
                      key: LIVECHAT_SCREEN_TABS.OPEN,
                      label: (
                        <span>
                          <ConversationIcon />
                          {t('Open')}
                        </span>
                      ),
                      children: <LiveChatLayout />
                    },
                    {
                      key: LIVECHAT_SCREEN_TABS.CLOSED,
                      label: (
                        <span>
                          <ContainerOutlined />
                          {t('Closed')}
                        </span>
                      ),
                      children: <LiveChatLayout />
                    }
                  ]
                  : []),
                {
                  key: LIVECHAT_SCREEN_TABS.REQUESTS_HISTORY,
                  label: null,
                  children: <LiveChatLayout />
                },
                {
                  key: LIVECHAT_SCREEN_TABS.SETTINGS,
                  label: null
                },
                {
                  key: LIVECHAT_SCREEN_TABS.ADMIN_SETTINGS,
                  label: null
                }
              ]}
            />
            )}
        </LiveChatContext.Provider>
      </Permissioned>
    </Fragment>
  );
};

LiveChatScreen.propTypes = {
  location: PropTypes.object
};

export default LiveChatScreen;
